import React, {useCallback, useMemo, useState} from 'react';
import {modelMetadataOptionsLabels} from '../../../../../store/modelMetadata';
import {MetadataOptionDTO, ModelMetadataOptionsDTOOptions, MetadataCountDTO} from 'hemwb-api';
import {ModelDTO} from 'hemwb-api/dist/models/ModelDTO';
import {ManageMetadataActionProps} from '../common/ActionMenuTypes';
import {Button, Drawer, Dropdown, Table} from 'antd';
import {MoreOutlined, EditOutlined, PoweroffOutlined, DeleteOutlined} from '@ant-design/icons';
import type {ColumnsType, TablePaginationConfig} from 'antd/es/table';
import type {MenuProps} from 'antd';
import type {SorterResult} from 'antd/es/table/interface';
import {canManageMetadata} from '../../../../../permissions/usePermissions';
import {tid} from '../../../../../testUtils';
import AddOption from '../common/addOption/AddOption';
import UpdateOption from '../common/updateOption/UpdateOption';
import RetireOption from '../common/retireOption/RetireOption';
import DeleteOption from '../common/deleteOption/DeleteOption';
import styles from './Grid.module.scss';

type ActionType = 'add' | 'edit' | 'retire' | 'delete';

type MetadataPropertyProps = {
  property: keyof ModelMetadataOptionsDTOOptions;
  onSelectChange: (selectedValues: Partial<ManageMetadataActionProps>) => void;
  selectedValues?: Partial<ManageMetadataActionProps>;
  searchValue: string;
  usages: {[key: string]: ModelDTO[]} | null;
  options: ModelMetadataOptionsDTOOptions;
  fileTagCount: MetadataCountDTO[] | undefined;
};

type RowData = (MetadataOptionDTO | MetadataCountDTO) & {usage?: number};

const MetadataGrid: React.FC<MetadataPropertyProps> = ({
  options,
  property,
  onSelectChange,
  searchValue,
  usages,
  fileTagCount,
}) => {
  const [pagination, setPagination] = useState<{current: number; pageSize: number}>({current: 1, pageSize: 10});
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('ascend');
  const [drawerAction, setDrawerAction] = useState<ActionType | null>(null);
  const [drawerRow, setDrawerRow] = useState<RowData | null>(null);

  const isFileTypes = modelMetadataOptionsLabels[property] === 'File Types';

  const rows: RowData[] = useMemo(() => {
    const lcSearch = searchValue?.toLocaleLowerCase() || '';
    if (isFileTypes) {
      const base = fileTagCount || [];
      const filtered = lcSearch ? base.filter((o) => o.value.toLocaleLowerCase().includes(lcSearch)) : base;
      return filtered.map((row) => ({
        ...row,
        usage: (row as MetadataCountDTO).count ?? 0,
      }));
    }
    const base = (options && options[property]) || [];
    const filtered = lcSearch ? base.filter((o) => o.value.toLocaleLowerCase().includes(lcSearch)) : base;
    return filtered.map((row) => ({
      ...row,
      usage: usages ? usages[row.value]?.length ?? 0 : 0,
    }));
  }, [isFileTypes, fileTagCount, options, property, usages, searchValue]);

  const sortedRows = useMemo(() => {
    if (!sortField) return rows;
    return [...rows].sort((a, b) => {
      let aVal: any;
      let bVal: any;
      if (sortField === 'usage') {
        aVal = (a as RowData).usage ?? 0;
        bVal = (b as RowData).usage ?? 0;
      } else if (sortField === 'retired') {
        aVal = a.retired ? 1 : 0;
        bVal = b.retired ? 1 : 0;
      } else {
        aVal = String(a.value ?? '').toLocaleLowerCase();
        bVal = String(b.value ?? '').toLocaleLowerCase();
      }
      if (sortOrder === 'ascend') return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    });
  }, [rows, sortField, sortOrder, isFileTypes]);

  const handleTableChange = (
    pag: TablePaginationConfig,
    _: Record<string, unknown>,
    sorter: SorterResult<RowData> | SorterResult<RowData>[],
  ) => {
    if (pag.current) setPagination({current: pag.current, pageSize: pag.pageSize || 10});
    if (!Array.isArray(sorter) && sorter.field) {
      setSortField(sorter.field as string);
      setSortOrder((sorter.order as 'ascend' | 'descend') || 'ascend');
    } else {
      setSortField(null);
    }
  };

  const openDrawer = useCallback((action: ActionType, row: RowData) => {
    setDrawerRow(row);
    setDrawerAction(action);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerAction(null);
    setDrawerRow(null);
  }, []);

  const handleSuccess = useCallback(
    (_selectNewValue?: string | null) => {
      closeDrawer();
      onSelectChange({value: undefined, retired: false});
    },
    [closeDrawer, onSelectChange],
  );

  const getMenuItems = useCallback(
    (row: RowData): MenuProps['items'] => {
      const usageCount = (row as RowData).usage ?? 0;
      const fileTagUsageCount = fileTagCount?.find((ft) => ft.value === row.value)?.count;
      const canManage = canManageMetadata(property);
      return [
        {
          key: 'edit',
          icon: <EditOutlined style={{padding: '10px'}} />,
          label: 'Edit Option',
          disabled: !canManage || !row.value || row.retired,
        },
        {type: 'divider'},
        {
          key: 'retire',
          icon: <PoweroffOutlined style={{padding: '10px', color: '#ff4d4f'}} />,
          label: <span style={{color: '#ff4d4f'}}>Retire Option</span>,
          disabled: !canManage || !row.value || row.retired,
        },
        {
          key: 'delete',
          icon: <DeleteOutlined style={{padding: '10px', color: '#ff4d4f'}} />,
          label: <span style={{color: '#ff4d4f'}}>Delete Option</span>,
          disabled:
            !canManage ||
            !row.value ||
            usageCount !== 0 ||
            row.retired ||
            (typeof fileTagUsageCount !== 'undefined' && fileTagUsageCount > 0),
        },
      ];
    },
    [fileTagCount, property],
  );

  const antdColumns: ColumnsType<RowData> = useMemo(
    () => [
      {title: modelMetadataOptionsLabels[property], dataIndex: 'value', key: 'value', sorter: true, width: '35%'},
      {
        title: 'Usage Count',
        dataIndex: 'usage',
        key: 'usage',
        sorter: true,
        width: '25%',
        render: (usage) => usage ?? 0,
      },
      {
        title: 'Retired',
        dataIndex: 'retired',
        key: 'retired',
        sorter: true,
        width: '20%',
        render: (_, row) => (row.retired ? 'Yes' : 'No'),
      },
      {
        title: '',
        key: 'actions',
        width: '10%',
        align: 'right' as const,
        render: (_, row) => (
          <Dropdown
            menu={{items: getMenuItems(row), onClick: ({key}) => openDrawer(key as ActionType, row)}}
            trigger={['click']}
            overlayClassName={styles.actionsMenuDropdown}
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
              {...tid('btn', 'metadata-row-actions', row.value)}
            />
          </Dropdown>
        ),
      },
    ],
    [property, isFileTypes, getMenuItems, openDrawer],
  );

  const drawerProps = useMemo((): ManageMetadataActionProps | null => {
    if (!drawerRow || !drawerAction) return null;
    const usageCount = (drawerRow as RowData).usage ?? 0;
    return {
      property,
      value: drawerRow.value,
      retired: drawerRow.retired,
      usageCount,
      onSuccess: handleSuccess,
      onCancel: closeDrawer,
      fileTagCount,
    };
  }, [drawerRow, drawerAction, isFileTypes, property, handleSuccess, closeDrawer, fileTagCount]);

  return (
    <div className={styles.listPanel}>
      <Table<RowData>
        columns={antdColumns}
        dataSource={sortedRows}
        rowKey={(row) => row.value}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: sortedRows.length,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
        locale={{emptyText: 'No options found'}}
        {...tid('metadata-grid-table')}
      />

      <Drawer
        open={!!drawerAction}
        onClose={closeDrawer}
        placement="right"
        width={420}
        className="whiteTheme"
        title={null}
        closable={false}
        styles={{body: {padding: '2.4rem', paddingTop: '1.6rem'}}}
      >
        {drawerProps && drawerAction === 'add' && <AddOption {...drawerProps} />}
        {drawerProps && drawerAction === 'edit' && <UpdateOption {...drawerProps} />}
        {drawerProps && drawerAction === 'retire' && <RetireOption {...drawerProps} />}
        {drawerProps && drawerAction === 'delete' && <DeleteOption {...drawerProps} />}
      </Drawer>
    </div>
  );
};

export default MetadataGrid;
